import { apiFetch, api } from "./api";
import { createDevConsole } from "../utils/devLogger";

const console = createDevConsole();

const APPLICATION_SEARCH_MIN_CHARS = 2;
const APPLICATION_SEARCH_CACHE_TTL_MS = 60_000;
const applicationSearchCache = new Map();

// Simple API service without client-side caching
// Server-side caching handles performance and consistency
class EntityAPIService {
  async getEntities() {
    try {
      console.log("🔍 EntityAPI: Loading entities from server");
      // Use paginated fetch with a reasonable cap to avoid huge payloads
      const response = await apiFetch(
        "/api/entities?page_size=200&architecture_explorer=true",
        {
          timeout: 15000,
        }
      );

      if (response.ok) {
        const data = await response.json();
        const entities = data.data || data.entities || [];
        console.log(
          `🔍 EntityAPI: Loaded ${entities.length} entities from server`
        );
        return entities;
      } else {
        console.warn("EntityAPI: Server returned error:", response.status);
        return [];
      }
    } catch (error) {
      console.error("EntityAPI: Error loading entities:", error);
      // Fallback 1: cached endpoint
      try {
        const cachedResp = await apiFetch(
          "/api/entities-cached?page=1&page_size=200&architecture_explorer=true",
          { timeout: 10000 }
        );
        if (cachedResp.ok) {
          const data = await cachedResp.json();
          const entities = data?.data?.entities || [];
          console.log(
            `🔍 EntityAPI: Loaded ${entities.length} entities from cached endpoint`
          );
          return entities;
        }
      } catch {}
      // Fallback 2: compat all-entities endpoint via axios service
      try {
        const all = await api.getEntitiesAll();
        const entities = all?.entities || [];
        console.log(
          `🔍 EntityAPI: Loaded ${entities.length} entities from compat endpoint`
        );
        return entities;
      } catch {}
      return [];
    }
  }

  // Get entities for autocomplete (with search filtering)
  async getEntitiesForAutocomplete(searchTerm = "") {
    // Use backend search endpoint for scalable autocomplete
    try {
      const url = `/api/entities/search?q=${encodeURIComponent(searchTerm)}&limit=50&offset=0`;
      const response = await apiFetch(url, { timeout: 15000 });
      if (response.ok) {
        const data = await response.json();
        const results = data.data || [];
        return results;
      }
    } catch (e) {
      console.warn(
        "EntityAPI: search endpoint failed, falling back to small fetch",
        e
      );
    }
    // Fallback: small local filter on a capped list
    const entities = await this.getEntities();
    if (!searchTerm) return entities.slice(0, 50);
    const q = searchTerm.toLowerCase();
    return entities
      .filter(
        (e) =>
          e.name?.toLowerCase().includes(q) ||
          (e.description && e.description.toLowerCase().includes(q))
      )
      .slice(0, 50);
  }

  /**
   * Get entity by ID.
   * @param {string} id
   * @param {{ includeRelationships?: boolean, includeRelatedEntities?: boolean, includeUnapproved?: boolean }} [options]
   *        When set, forwarded as query params to GET /api/entities/{id} (same contract as services/api.getEntity).
   */
  async getEntityById(id, options = {}) {
    const {
      includeRelationships = false,
      includeRelatedEntities = false,
      includeUnapproved = false,
    } = options;
    const qs = new URLSearchParams();
    if (includeRelationships) qs.set("include_relationships", "true");
    if (includeRelatedEntities) qs.set("include_related_entities", "true");
    if (includeUnapproved) qs.set("includeUnapproved", "true");
    const suffix = qs.toString() ? `?${qs.toString()}` : "";

    try {
      const response = await apiFetch(`/api/entities/${id}${suffix}`, {
        timeout: 10000,
      });
      if (response.ok) {
        const entity = await response.json();
        console.log(
          `🔍 EntityAPI: Successfully fetched entity ${id}:`,
          entity.name
        );
        return entity;
      } else {
        console.warn(
          `🔍 EntityAPI: Entity ${id} not found (status ${response.status})`
        );
        // Try fallback to local cache
        const entities = await this.getEntities();
        const found = entities.find((entity) => entity.id === id);
        if (found) {
          console.log(`🔍 EntityAPI: Found entity ${id} in local cache`);
          return found;
        }
        console.warn(`🔍 EntityAPI: Entity ${id} not found in cache either`);
        return null;
      }
    } catch (error) {
      console.error(`🔍 EntityAPI: Error fetching entity ${id}:`, error);
      // Try fallback to local cache
      try {
        const entities = await this.getEntities();
        const found = entities.find((entity) => entity.id === id);
        if (found) {
          console.log(
            `🔍 EntityAPI: Found entity ${id} in local cache after error`
          );
          return found;
        }
      } catch (cacheError) {
        console.error(`🔍 EntityAPI: Error accessing cache:`, cacheError);
      }
      return null;
    }
  }

  // Get entities by type
  async getEntitiesByType(type) {
    const entities = await this.getEntities();
    return entities.filter(
      (entity) => (entity.entity_type || entity.type) === type
    );
  }

  /**
   * Search applications by name/description (typeahead).
   * Uses /api/entities-cached with in-memory tenant cache on the server; short client cache for repeat queries.
   */
  async searchApplications(searchTerm, { limit = 25 } = {}) {
    const q = String(searchTerm || "").trim();
    if (q.length < APPLICATION_SEARCH_MIN_CHARS) {
      return [];
    }

    const cacheKey = `${q.toLowerCase()}|${limit}`;
    const cached = applicationSearchCache.get(cacheKey);
    if (cached && Date.now() - cached.ts < APPLICATION_SEARCH_CACHE_TTL_MS) {
      return cached.results;
    }

    const storeResults = (results) => {
      applicationSearchCache.set(cacheKey, { ts: Date.now(), results });
      if (applicationSearchCache.size > 50) {
        const oldest = applicationSearchCache.keys().next().value;
        applicationSearchCache.delete(oldest);
      }
      return results;
    };

    const isApprovedApplication = (entity) => {
      const t = String(entity.entity_type || entity.type || "").toLowerCase();
      if (t !== "application" && !t.includes("application")) return false;
      const status = String(entity.approval_status || "Approved").toLowerCase();
      return status === "approved";
    };

    const cachedUrl = `/api/entities-cached?page=1&page_size=${limit}&entity_type=application&search=${encodeURIComponent(q)}`;
    try {
      const response = await apiFetch(cachedUrl, { timeout: 10000 });
      if (response.ok) {
        const data = await response.json();
        const entities = (data?.data?.entities || []).filter(isApprovedApplication);
        if (entities.length) {
          console.log(
            `🔍 EntityAPI: Found ${entities.length} applications for "${q}" via cached endpoint`
          );
          return storeResults(entities);
        }
      }
    } catch (e) {
      console.warn("EntityAPI: searchApplications cached endpoint failed", e);
    }

    try {
      const url = `/api/entities/search?q=${encodeURIComponent(q)}&entity_type=application&limit=${limit}`;
      const response = await apiFetch(url, { timeout: 10000 });
      if (response.ok) {
        const data = await response.json();
        const results = (data.data || []).filter(isApprovedApplication).slice(0, limit);
        console.log(
          `🔍 EntityAPI: Found ${results.length} applications for "${q}" via search endpoint`
        );
        return storeResults(results);
      }
    } catch (e) {
      console.warn("EntityAPI: searchApplications search endpoint failed", e);
    }

    return storeResults([]);
  }

  /**
   * Search allowlisted enrichment anchors (apps, orgs, capabilities, …).
   * Pass entityTypes to filter (e.g. ["organization"]); defaults to all allowlisted types.
   */
  async searchEnrichmentAnchors(searchTerm, { limit = 25, entityTypes = null } = {}) {
    const {
      ENRICHMENT_ANCHOR_TYPES,
      isEnrichmentAnchorType,
      normalizeEnrichmentAnchorType,
    } = await import("../config/enrichmentAnchorTypes");

    const q = String(searchTerm || "").trim();
    if (q.length < APPLICATION_SEARCH_MIN_CHARS) {
      return [];
    }

    const types = (entityTypes && entityTypes.length
      ? entityTypes
      : ENRICHMENT_ANCHOR_TYPES
    )
      .map(normalizeEnrichmentAnchorType)
      .filter(isEnrichmentAnchorType);

    if (!types.length) {
      return [];
    }

    const cacheKey = `enrich|${q.toLowerCase()}|${types.join(",")}|${limit}`;
    const cached = applicationSearchCache.get(cacheKey);
    if (cached && Date.now() - cached.ts < APPLICATION_SEARCH_CACHE_TTL_MS) {
      return cached.results;
    }

    const storeResults = (results) => {
      applicationSearchCache.set(cacheKey, { ts: Date.now(), results });
      if (applicationSearchCache.size > 50) {
        const oldest = applicationSearchCache.keys().next().value;
        applicationSearchCache.delete(oldest);
      }
      return results;
    };

    const isApprovedEnrichmentAnchor = (entity) => {
      const t = normalizeEnrichmentAnchorType(
        entity.entity_type || entity.type || ""
      );
      if (!types.includes(t)) return false;
      const status = String(entity.approval_status || "Approved").toLowerCase();
      return status === "approved";
    };

    const fetchType = async (entityType, pageSize) => {
      const cachedUrl = `/api/entities-cached?page=1&page_size=${pageSize}&entity_type=${encodeURIComponent(
        entityType
      )}&search=${encodeURIComponent(q)}`;
      try {
        const response = await apiFetch(cachedUrl, { timeout: 10000 });
        if (response.ok) {
          const data = await response.json();
          const entities = (data?.data?.entities || []).filter(
            isApprovedEnrichmentAnchor
          );
          // Empty cached hit: fall through to search (cache can omit non-app types).
          if (entities.length) {
            return entities;
          }
        }
      } catch (e) {
        console.warn(
          `EntityAPI: searchEnrichmentAnchors cached failed for ${entityType}`,
          e
        );
      }
      try {
        const url = `/api/entities/search?q=${encodeURIComponent(
          q
        )}&entity_type=${encodeURIComponent(entityType)}&limit=${pageSize}`;
        const response = await apiFetch(url, { timeout: 10000 });
        if (response.ok) {
          const data = await response.json();
          return (data.data || []).filter(isApprovedEnrichmentAnchor);
        }
      } catch (e) {
        console.warn(
          `EntityAPI: searchEnrichmentAnchors search failed for ${entityType}`,
          e
        );
      }
      return [];
    };

    const perType =
      types.length === 1
        ? limit
        : Math.max(8, Math.ceil(limit / Math.max(types.length, 1)));
    const batches = await Promise.all(types.map((t) => fetchType(t, perType)));

    const byId = new Map();
    for (const batch of batches) {
      for (const ent of batch) {
        if (ent?.id && !byId.has(ent.id)) byId.set(ent.id, ent);
      }
    }
    const results = [...byId.values()]
      .sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), undefined, {
          sensitivity: "base",
        })
      )
      .slice(0, limit);
    return storeResults(results);
  }

  /** Applications for pickers (Add knowledge anchor, etc.) — server cache first. */
  async getApplications() {
    const cachedUrl =
      "/api/entities-cached?page=1&page_size=500&entity_type=application&architecture_explorer=true";
    try {
      const response = await apiFetch(cachedUrl, { timeout: 10000 });
      if (response.ok) {
        const data = await response.json();
        const entities = data?.data?.entities || [];
        if (entities.length) {
          console.log(
            `🔍 EntityAPI: Loaded ${entities.length} applications from cached endpoint`
          );
          return entities;
        }
      }
    } catch (e) {
      console.warn("EntityAPI: applications cached endpoint failed", e);
    }
    try {
      const response = await apiFetch(
        "/api/entities?page=1&page_size=500&type=application&architecture_explorer=true",
        { timeout: 15000 }
      );
      if (response.ok) {
        const data = await response.json();
        const entities = data.entities || [];
        console.log(
          `🔍 EntityAPI: Loaded ${entities.length} applications from entities endpoint`
        );
        return entities;
      }
    } catch (e) {
      console.warn("EntityAPI: applications entities endpoint failed", e);
    }
    const entities = await this.getEntities();
    return entities.filter((entity) => {
      const t = String(entity.entity_type || entity.type || "").toLowerCase();
      return t === "application" || t.includes("application");
    });
  }

  // Get entities by IDs in bulk (for graph visualization)
  async getEntitiesByIds(entityIds) {
    if (!entityIds || entityIds.length === 0) {
      return [];
    }
    try {
      console.log(
        `🔍 EntityAPI: Fetching ${entityIds.length} entities by IDs in bulk`
      );
      const response = await apiFetch("/api/entities/bulk", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ entity_ids: entityIds }),
        timeout: 20000,
      });

      if (response.ok) {
        const data = await response.json();
        const entities = data.entities || [];
        console.log(
          `🔍 EntityAPI: Loaded ${entities.length} entities from bulk endpoint`
        );
        return entities;
      } else {
        console.warn(
          "EntityAPI: Bulk endpoint returned error:",
          response.status
        );
        return [];
      }
    } catch (error) {
      console.error("EntityAPI: Error loading entities by IDs:", error);
      // Fallback to individual fetches (but limit to avoid too many requests)
      if (entityIds.length <= 10) {
        const results = await Promise.allSettled(
          entityIds.map((id) => this.getEntityById(id))
        );
        return results
          .filter((r) => r.status === "fulfilled" && r.value)
          .map((r) => r.value);
      }
      return [];
    }
  }
}

// Export singleton instance
export const entityAPI = new EntityAPIService();
